import { clamp, sum, unnest } from 'ramda';
import { isBoosterNode, isChokeNode } from '../Nodes';
import PCNode from '../Nodes/PCNode';
import PCSpool from '../Spools/PCSpool';
import { FLUSH_VOLUME_TOLERANCE } from './flushConstants';
import {
  createFrictionModel,
  FlushLine,
  FrictionModel,
  frontState,
  handoverPoint,
  PressureWalk,
  requiredPressure,
  scanPositions,
  toProfilePoints,
  walkPressure
} from './flushPressure';
import {
  FlushProfile,
  FlushResult,
  FlushSpec,
  FlushStage,
  FlushStageType,
  FlushVolumeMode,
  FlushWarning,
  FlushWarningCode,
  PasteProperties
} from './flushTypes';
import {
  buildVolumeTable,
  frontFromVolume,
  resolveFinalFlushVolume,
  segmentsPlantToStope,
  volumeFromFront
} from './flushVolume';

// What the solver needs from a PCRoute: its spools and node ids, both in stope -> plant order.
export interface FlushRouteInput {
  spools: PCSpool[];
  routePointStrings: string[];
}

const WARNING_DESCRIPTIONS: Record<FlushWarningCode, string> = {
  FLUSH_PUMP_SHORTFALL: 'The flush pump cannot take over at any point of the planned flush; all of the water goes through the paste pump.',
  AIR_START_NOT_REACHED: 'The surface pressure never falls to the air purge pressure within the planned water volume.',
  AIR_MODE_WITHOUT_AIR: 'The flush volume is set to "until the air purge can start" but the air purge is disabled; one line volume has been planned instead.',
  OVER_PRESSURE: 'The required pressure exceeds a pipe rating at some point during the flush.',
  NO_WATER_AT_STOPE: 'The planned water volume is less than the line volume and no air purge will push it on, so no water reaches the stope.',
};

const warning = (code: FlushWarningCode): FlushWarning => ({ code, description: WARNING_DESCRIPTIONS[code] });

// Reduces the route to plain numbers. Distances are measured from the plant and sum the spool
// lengths (choke loops included), so the plant node is at exactly 0 and the discharge at the total.
export const buildFlushLine = ({ spools, routePointStrings }: FlushRouteInput): FlushLine => {
  const nodeLookup = spools.reduce<Record<string, PCNode>>(
    (acc, spool) => ({ ...acc, [spool.pointA.id]: spool.pointA, [spool.pointB.id]: spool.pointB }),
    {},
  );
  const lengths = spools.map((spool) => spool.length());
  const totalLength = sum(lengths);
  const distances = lengths.reduce<number[]>((acc, length) => [...acc, Math.max(acc[acc.length - 1] - length, 0)], [totalLength]);

  const nodes = routePointStrings.map((id, i) => {
    const node = nodeLookup[id];
    if (!node) throw new Error(`Node ${id} not found in spools`);
    return {
      id,
      distanceFromPump: distances[i],
      elevation: node.coords.z,
      isBooster: isBoosterNode(id),
      isChoke: isChokeNode(id),
      chokeHead: node.chokeHead,
    };
  });
  const segments = spools.map((spool, i) => ({
    insideDiameter: spool.pipe?.insideDiameter ?? 0,
    pressureRating: spool.pipe?.pressureRating ?? Number.POSITIVE_INFINITY,
    length: lengths[i],
  }));

  return { nodes, segments, totalLength, table: buildVolumeTable(segmentsPlantToStope(spools)) };
};

const assertPositive = (value: number, name: string) => {
  if (!(value > 0) || !Number.isFinite(value)) throw new Error(`${name} must be a positive number, got ${value}`);
};

const minutesFor = (volume: number, flowRate: number): number => (volume / flowRate) * 60;

// Time, in minutes from the start of the flush, at which the cumulative water pumped reaches `volume`.
export const timeAtVolume = (stages: FlushStage[], volume: number): number => stages
  .reduce((minutes, stage) => minutes + minutesFor(clamp(0, stage.endVolume - stage.startVolume, volume - stage.startVolume), stage.flowRate), 0);

export const stageAtVolume = (stages: FlushStage[], volume: number): FlushStage | undefined => stages
  .find((stage) => volume <= stage.endVolume) ?? stages[stages.length - 1];

export const solveFlushLine = (line: FlushLine, spec: FlushSpec, paste: PasteProperties): FlushResult => {
  assertPositive(spec.flushPumpFlowRate, 'flushPumpFlowRate');
  assertPositive(spec.pastePumpFlowRate, 'pastePumpFlowRate');

  const friction = createFrictionModel(paste);
  const { table } = line;
  const lineVolume = table.totalVolume;
  const lineLength = table.totalLength;
  const pressureAt = (front: number, flowRate: number): number => requiredPressure(line, friction, paste, front, flowRate);

  const airLimitKpa = spec.airFlushMaxPressureKpa ?? 0;
  const airEnabled = spec.airFlushEnabled && airLimitKpa > 0;
  const airStartAt = (endDistance: number): number | undefined => (airEnabled
    ? handoverPoint(line, friction, paste, airLimitKpa, spec.flushPumpFlowRate, endDistance)
    : undefined);

  // (c) Final volume. The air-start mode needs the air-start point over the whole line first.
  const wantsAirMode = spec.volumeMode === FlushVolumeMode.AirStartVolume;
  const airStartFullLine = wantsAirMode ? airStartAt(lineLength) : undefined;
  const resolved = resolveFinalFlushVolume(
    spec.volumeMode,
    spec.volumeValue,
    table,
    airStartFullLine !== undefined ? volumeFromFront(table, airStartFullLine) : undefined,
  );
  const finalVolume = resolved ?? lineVolume;
  const endDistance = frontFromVolume(table, Math.min(finalVolume, lineVolume));

  // (a) Paste-pump handover, tested at the flush pump's flow rate because that is the pump that
  // has to take over.
  const handover = handoverPoint(line, friction, paste, spec.flushPumpMaxPressureKpa, spec.flushPumpFlowRate, endDistance);
  const pastePumpWaterVolume = handover === undefined ? finalVolume : volumeFromFront(table, handover);
  const pastePumpHandoverDistance = handover ?? endDistance;
  const flushPumpCanCompleteAlone = handover !== undefined && pastePumpWaterVolume <= 0;

  const stageFor = (stageType: FlushStageType, startVolume: number, endVolume: number, flowRate: number): FlushStage => {
    const startDistance = frontFromVolume(table, startVolume);
    const stageEndDistance = frontFromVolume(table, endVolume);
    return {
      stageType,
      startVolume,
      endVolume,
      startDistance,
      endDistance: stageEndDistance,
      flowRate,
      requiredPressureStartKpa: pressureAt(startDistance, flowRate),
      requiredPressureEndKpa: pressureAt(stageEndDistance, flowRate),
      durationMinutes: minutesFor(endVolume - startVolume, flowRate),
    };
  };
  const pasteEnd = Math.min(pastePumpWaterVolume, finalVolume);
  const stages: FlushStage[] = [
    ...(pasteEnd > 0 ? [stageFor(FlushStageType.PastePump, 0, pasteEnd, spec.pastePumpFlowRate)] : []),
    ...(finalVolume > pasteEnd ? [stageFor(FlushStageType.FlushPump, pasteEnd, finalVolume, spec.flushPumpFlowRate)] : []),
  ];

  // Sweep each stage at its own flow rate for the pressure envelope and any pipe over-pressure.
  const sweeps: PressureWalk[] = unnest(stages.map((stage) => [
    stage.startDistance,
    ...scanPositions(line, stage.endDistance).filter((front) => front > stage.startDistance),
  ].map((front) => walkPressure(line, friction, paste, front, stage.flowRate))));
  const maxRequiredPressureKpa = sweeps.reduce((maximum, walk) => Math.max(maximum, walk.plantPressureKpa), 0);
  const overPressure = sweeps.some((walk) => walk.overPressure);

  // (b) Air start within the planned flush.
  const airStart = airStartAt(endDistance);
  const airStartWaterVolume = airStart !== undefined ? volumeFromFront(table, airStart) : undefined;

  const reachesStope = finalVolume >= lineVolume * (1 - FLUSH_VOLUME_TOLERANCE);
  // Short of a full line, the air purge still carries the water on once it can start; its timing is not modelled.
  const airPushesWater = !reachesStope && airStart !== undefined;

  const warnings: FlushWarning[] = [
    ...(wantsAirMode && !airEnabled ? [warning('AIR_MODE_WITHOUT_AIR')] : []),
    ...(handover === undefined ? [warning('FLUSH_PUMP_SHORTFALL')] : []),
    ...(airEnabled && airStart === undefined ? [warning('AIR_START_NOT_REACHED')] : []),
    ...(overPressure ? [warning('OVER_PRESSURE')] : []),
    ...(reachesStope || airPushesWater ? [] : [warning('NO_WATER_AT_STOPE')]),
  ];

  return {
    totalLineVolume: lineVolume,
    totalLineLength: lineLength,
    finalFlushVolume: finalVolume,
    pastePumpWaterVolume,
    pastePumpHandoverDistance,
    flushPumpCanCompleteAlone,
    airStartWaterVolume,
    airStartDistance: airStart,
    airStartTimeMinutes: airStartWaterVolume !== undefined ? timeAtVolume(stages, airStartWaterVolume) : undefined,
    airStartStage: airStartWaterVolume !== undefined ? stageAtVolume(stages, airStartWaterVolume)?.stageType : undefined,
    timeToWaterAtStopeMinutes: reachesStope ? timeAtVolume(stages, lineVolume) : undefined,
    waterAtStopeStage: reachesStope ? stageAtVolume(stages, lineVolume)?.stageType : undefined,
    waterFrontStopDistance: endDistance,
    maxRequiredPressureKpa,
    stages,
    warnings,
  };
};

export const solveFlush = (route: FlushRouteInput, spec: FlushSpec, paste: PasteProperties): FlushResult => solveFlushLine(buildFlushLine(route), spec, paste);

// The state of the line after `waterVolume` m3 of water, for the HGL overlay and the scrub control.
export const simulateFlush = (
  line: FlushLine,
  spec: FlushSpec,
  paste: PasteProperties,
  result: FlushResult,
  waterVolume: number,
  friction: FrictionModel = createFrictionModel(paste),
): FlushProfile => {
  const volume = clamp(0, result.finalFlushVolume, waterVolume);
  const front = frontFromVolume(line.table, volume);
  const drivingPump = volume < result.pastePumpWaterVolume ? FlushStageType.PastePump : FlushStageType.FlushPump;
  const flowRate = drivingPump === FlushStageType.PastePump ? spec.pastePumpFlowRate : spec.flushPumpFlowRate;
  const walk = walkPressure(line, friction, paste, front, flowRate);
  const atFront = frontState(line, friction, paste, walk, front, flowRate);
  return {
    waterVolume: volume,
    frontDistance: front,
    frontPressureKpa: atFront.pressureKpa,
    frontElevation: atFront.elevation,
    drivingPump,
    flowRate,
    requiredPressureKpa: walk.plantPressureKpa,
    airMayStart: result.airStartWaterVolume !== undefined && volume >= result.airStartWaterVolume,
    points: toProfilePoints(line, walk, front),
  };
};
